import { notification } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import APIClient from '../api/api';
import { GetListingsResponse, Listing, ListingStatus } from '../api/models';
import {
  Button,
  Column,
  DataTable,
  DomainName,
  Num,
  Pagination,
  StatusTag,
  TagTone,
} from '../components/ui';
import { PageFooter, PageHead, PageToolbar } from '../layout';
import { ImportModal, ImportModalConfig } from '../components/ImportModal';
import { SelectPill, TextPill } from '../components/FilterPills';

const parity1ListingsConfig: ImportModalConfig = {
  title: 'Send Parity1 Listings Email',
  description: 'Upload a file to immediately send the Parity1 listings email.',
  action: '/v1/afternic/sendParity1Listings',
  successMessage: 'Parity1 listings email sent.',
  errorMessage: 'Parity1 listings email failed to send.',
  callout: {
    tone: 'warn',
    body: 'The email will be sent as soon as the file is uploaded.',
  },
};

const DEFAULT_PAGE_SIZE = 25;

const venueFilterOptions: { value: string; label: string }[] = [
  { value: '', label: 'All Venues' },
  { value: 'afternic', label: 'Afternic' },
  { value: 'sedo', label: 'Sedo' },
  { value: 'dan', label: 'Dan' },
  { value: 'atom', label: 'Atom' },
];

const statusFilterOptions: { value: string; label: string }[] = [
  { value: '', label: 'All Statuses' },
  { value: 'listed', label: 'Listed' },
  { value: 'sold', label: 'Sold' },
  { value: 'needsTransfer', label: 'Needs Transfer' },
  { value: 'payoutPending', label: 'Payout Pending' },
  { value: 'complete', label: 'Complete' },
  { value: 'cancelled', label: 'Cancelled' },
];

const statusTone: Record<ListingStatus, TagTone> = {
  listed: 'accent',
  sold: 'success',
  needsTransfer: 'warn',
  payoutPending: 'warn',
  complete: 'success',
  cancelled: 'danger',
};

const Listings = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const ltoActive = searchParams.get('ltoActive') === 'true';
  const domain = searchParams.get('domain') || '';
  const venue = searchParams.get('venue') || '';
  const status = searchParams.get('status') || '';
  const [data, setData] = useState<GetListingsResponse>();
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [showParity1Import, setShowParity1Import] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    loadListings(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageSize, ltoActive, domain, venue, status]);

  function setParam(key: string, value: string) {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next);
  }

  async function loadListings(p: number) {
    setLoading(true);
    try {
      const resp = await APIClient.getListings(
        { page: p, pageSize },
        { ltoActive, domain, venue, status },
      );
      setData(resp);
      setPage(resp.currentPage || p);
    } catch (e) {
      console.error(e);
      notification.error({ message: 'Failed to load listings.' });
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <PageHead
        title="Listings"
        subtitle="Active and historical sales venue listings across all accounts."
        actions={
          <>
            <Button variant="ghost" onClick={() => setShowParity1Import(true)}>
              Parity1 Email
            </Button>
            <Button
              variant="ghost"
              loading={downloading}
              onClick={async () => {
                setDownloading(true);
                try {
                  const resp = await APIClient.instance.get('/v1/sales/report/missingPayouts', { responseType: 'blob' });
                  const a = document.createElement('a');
                  a.href = URL.createObjectURL(resp.data);
                  a.download = 'missing_payouts.csv';
                  a.click();
                  URL.revokeObjectURL(a.href);
                } catch (e) {
                  notification.error({ message: 'Download failed.' });
                } finally {
                  setDownloading(false);
                }
              }}
            >
              Missing Payouts Report
            </Button>
          </>
        }
      />
      <PageToolbar>
        <TextPill
          label="Domain"
          value={domain}
          placeholder="contains…"
          onChange={(v) => setParam('domain', v)}
        />
        <SelectPill
          label="Venue"
          value={venueFilterOptions.find((o) => o.value === venue)?.label || 'All'}
          options={venueFilterOptions}
          selected={venue}
          onChange={(v) => setParam('venue', v)}
        />
        <SelectPill
          label="Status"
          value={statusFilterOptions.find((o) => o.value === status)?.label || 'All'}
          options={statusFilterOptions}
          selected={status}
          onChange={(v) => setParam('status', v)}
        />
        {ltoActive && (
          <button
            type="button"
            onClick={() => setParam('ltoActive', '')}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium bg-accent-soft text-accent border border-accent/30 cursor-pointer hover:opacity-80"
          >
            Active LTOs ✕
          </button>
        )}
        <span className="ml-auto text-[11px] text-text-3 font-mono">
          {data ? <>total <b className="text-text-2">{data.total.toLocaleString()}</b></> : 'loading…'}
        </span>
      </PageToolbar>

      <DataTable
        columns={ltoActive ? ltoColumns : columns}
        data={data?.content ?? []}
        rowKey={(l) => String(l.id)}
        loading={loading}
        emptyMessage="No listings found."
      />
      <PageFooter
        left={
          <span>
            Showing <b className="text-text-2">{data?.content.length ?? 0}</b> of{' '}
            <b className="text-text-2">{data?.total.toLocaleString() ?? 0}</b>
          </span>
        }
        right={
          <Pagination
            page={page}
            pageSize={pageSize}
            total={data?.total ?? 0}
            onPageChange={loadListings}
            onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
            loading={loading}
          />
        }
      />

      {showParity1Import && (
        <ImportModal
          config={parity1ListingsConfig}
          onClose={() => setShowParity1Import(false)}
        />
      )}
    </>
  );
};

function domainCell(l: Listing) {
  return (
    <span className="inline-flex items-center gap-1.5 group">
      <DomainName name={l.domain} />
      <Link
        to={`/sales/activity?domain=${encodeURIComponent(l.domain)}`}
        title={`View sales activity for ${l.domain}`}
        className="text-text-3 hover:text-accent opacity-0 group-hover:opacity-100 transition-opacity"
      >
        <SearchOutlined style={{ fontSize: 11 }} />
      </Link>
    </span>
  );
}

const columns: Column<Listing>[] = [
  {
    key: 'domain',
    title: 'Domain',
    render: domainCell,
  },
  {
    key: 'venue',
    title: 'Venue',
    render: (l) => (
      <span className="text-text-2 text-[12.5px]">
        {l.venue}
        {l.account && <span className="text-text-3"> · {l.account}</span>}
      </span>
    ),
  },
  {
    key: 'listPrice',
    title: 'List Price',
    align: 'right',
    render: (l) => <Num value={l.listPrice ?? null} format="currency" />,
  },
  {
    key: 'salePrice',
    title: 'Sale Price',
    align: 'right',
    render: (l) => <Num value={l.salePrice ?? null} format="currency" />,
  },
  {
    key: 'payoutAmount',
    title: 'Payout',
    align: 'right',
    render: (l) => <Num value={l.payoutAmount ?? null} format="currency" tone="muted" />,
  },
  {
    key: 'status',
    title: 'Status',
    render: (l) => (
      <StatusTag tone={statusTone[l.status] ?? 'neutral'}>{prettyStatus(l.status)}</StatusTag>
    ),
  },
];

const ltoColumns: Column<Listing>[] = [
  {
    key: 'domain',
    title: 'Domain',
    render: domainCell,
  },
  {
    key: 'venue',
    title: 'Venue',
    render: (l) => (
      <span className="text-text-2 text-[12.5px]">
        {l.venue}
        {l.account && <span className="text-text-3"> · {l.account}</span>}
      </span>
    ),
  },
  {
    key: 'salePrice',
    title: 'Sale Price',
    align: 'right',
    render: (l) => <Num value={l.salePrice ?? null} format="currency" />,
  },
  {
    key: 'installmentAmount',
    title: 'Per Installment',
    align: 'right',
    render: (l) => <Num value={l.payoutAmount ?? null} format="currency" tone="muted" />,
  },
  {
    key: 'progress',
    title: 'Progress',
    render: (l) => {
      const paid = l.installmentsPaid ?? 0;
      const total = l.installmentsTotal ?? 0;
      const remaining = Math.max(0, total - paid);
      const pct = total > 0 ? Math.round((paid / total) * 100) : 0;
      return (
        <span className="font-mono text-[12px] text-text-2 tabular-nums">
          {paid}/{total} <span className="text-text-3">({pct}%)</span>
          <span className="text-text-3 ml-1">· {remaining} left</span>
        </span>
      );
    },
  },
  {
    key: 'outstanding',
    title: 'Outstanding',
    align: 'right',
    render: (l) => {
      const remaining = Math.max(0, (l.installmentsTotal ?? 0) - (l.installmentsPaid ?? 0));
      const outstanding = remaining * (l.payoutAmount ?? 0);
      return <Num value={outstanding} format="currency" />;
    },
  },
  {
    key: 'status',
    title: 'Status',
    render: (l) => (
      <StatusTag tone={statusTone[l.status] ?? 'neutral'}>{prettyStatus(l.status)}</StatusTag>
    ),
  },
];

function prettyStatus(status: ListingStatus): string {
  switch (status) {
    case 'needsTransfer':
      return 'Needs Transfer';
    case 'payoutPending':
      return 'Payout Pending';
    default:
      return status.charAt(0).toUpperCase() + status.slice(1);
  }
}

export default Listings;
